import dayjs, { Dayjs } from "dayjs";
import {
    collection, doc, DocumentData, getCountFromServer, getDoc, getDocs, onSnapshot, query,
    serverTimestamp, Timestamp, where, writeBatch
} from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import { auth, db } from "config/firebase";
import { QUIZ_COLLECTION } from "./quizData";

// Lo que la web consume: cuestionario publico (sin respuestas correctas).
export const QUIZZES_COLLECTION = "quizzes";
// Clave de respuestas separada para poder restringir su lectura con reglas.
export const QUIZ_KEYS_COLLECTION = "quiz_keys";
export const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F"];
export const MIN_OPTIONS = 2;
export const QUIZ_WEEKDAY = 2; // martes

export type QuizStatus = "draft" | "active" | "inactive";

export const statusLabels: Record<QuizStatus, { label: string; color: string }> = {
    draft: { label: "Borrador", color: "default" },
    active: { label: "Activo", color: "green" },
    inactive: { label: "Inactivo", color: "orange" }
};

export interface QuizOption {
    id: string;
    text: string;
}

export interface QuizQuestion {
    id: string;
    order: number;
    text: string;
    options: QuizOption[];
}

export interface GradeRange {
    min: number;
    max: number;
    label: string;
}

export interface Quiz {
    id: string;
    title: string;
    description: string;
    level: string;
    estimatedMinutes: number | null;
    quizDate: Date | null;
    quizDateKey: string;
    status: QuizStatus;
    questions: QuizQuestion[];
    questionCount: number;
    gradingScale: GradeRange[];
    createdAt: Date | null;
    updatedAt: Date | null;
}

// Forma editable de una pregunta en el BO: opciones como texto y la correcta por indice.
export interface DraftQuestion {
    id: string;
    text: string;
    options: string[];
    correctIndex: number;
}

export interface QuizMeta {
    title: string;
    description?: string;
    level?: string;
    estimatedMinutes?: number | null;
    quizDate: Dayjs;
}

const toDate = (value: any): Date | null => (value && typeof value.toDate === "function" ? value.toDate() : null);

export const newQuestionId = () => `q_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const emptyQuestion = (): DraftQuestion => ({ id: newQuestionId(), text: "", options: ["", "", "", ""], correctIndex: -1 });

export const nextQuizDate = (from: Dayjs = dayjs()) => {
    const diff = (QUIZ_WEEKDAY - from.day() + 7) % 7;
    return from.add(diff, "day").startOf("day");
};

// Misma proporcion que la escala del quiz original (18/16/14 de 20).
export const defaultGradingScale = (total: number): GradeRange[] => {
    if (!total) return [];
    const excellent = Math.ceil(total * 0.9);
    const approved = Math.ceil(total * 0.8);
    const reinforce = Math.ceil(total * 0.7);
    return [
        { min: excellent, max: total, label: "Excelente" },
        { min: approved, max: excellent - 1, label: "Aprobado" },
        { min: reinforce, max: approved - 1, label: "Reforzar conocimientos" },
        { min: 0, max: reinforce - 1, label: "Repetir capacitación" }
    ].filter((range) => range.min <= range.max);
};

export const normalizeQuiz = (id: string, data: DocumentData): Quiz => {
    const questions: QuizQuestion[] = (Array.isArray(data.questions) ? data.questions : [])
        .map((q: any, index: number) => ({
            id: String(q?.id ?? `q${index + 1}`),
            order: Number(q?.order ?? index + 1),
            text: String(q?.text ?? ""),
            options: (Array.isArray(q?.options) ? q.options : []).map((o: any, i: number) => ({
                id: String(o?.id ?? OPTION_LETTERS[i]),
                text: String(o?.text ?? "")
            }))
        }))
        .sort((a: QuizQuestion, b: QuizQuestion) => a.order - b.order);
    return {
        id,
        title: String(data.title ?? "Sin título"),
        description: String(data.description ?? ""),
        level: String(data.level ?? ""),
        estimatedMinutes: data.estimatedMinutes != null ? Number(data.estimatedMinutes) : null,
        quizDate: toDate(data.quizDate),
        quizDateKey: String(data.quizDateKey ?? ""),
        status: (["draft", "active", "inactive"].includes(data.status) ? data.status : "draft") as QuizStatus,
        questions,
        questionCount: Number(data.questionCount ?? questions.length),
        gradingScale: Array.isArray(data.gradingScale) ? data.gradingScale : [],
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt)
    };
};

export const toDraftQuestions = (quiz: Quiz, key: Record<string, string>): DraftQuestion[] => (
    quiz.questions.map((q) => ({
        id: q.id,
        text: q.text,
        options: q.options.map((o) => o.text),
        correctIndex: q.options.findIndex((o) => o.id === key[q.id])
    }))
);

export const validateQuestions = (questions: DraftQuestion[]) => {
    const errors: string[] = [];
    if (!questions.length) errors.push("Agrega al menos una pregunta.");
    questions.forEach((q, i) => {
        const n = i + 1;
        if (!q.text.trim()) errors.push(`Pregunta ${n}: falta el texto.`);
        const filled = q.options.filter((o) => o.trim());
        if (filled.length < MIN_OPTIONS || filled.length !== q.options.length) errors.push(`Pregunta ${n}: todas las opciones deben tener texto (mínimo ${MIN_OPTIONS}).`);
        if (q.correctIndex < 0 || q.correctIndex >= q.options.length) errors.push(`Pregunta ${n}: marca la respuesta correcta.`);
    });
    return errors;
};

// Reparte la respuesta correcta entre todas las letras de forma pareja y revuelve las demas opciones.
export const shuffleAnswers = (questions: DraftQuestion[]): DraftQuestion[] => {
    const random = <T>(list: T[]) => list[Math.floor(Math.random() * list.length)];
    const shuffle = <T>(list: T[]) => {
        const copy = [...list];
        for (let i = copy.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [copy[i], copy[j]] = [copy[j], copy[i]];
        }
        return copy;
    };
    const usage = new Array(OPTION_LETTERS.length).fill(0);
    const result = [...questions];

    shuffle(questions.map((_, i) => i)).forEach((qi) => {
        const q = questions[qi];
        if (q.correctIndex < 0) {
            result[qi] = { ...q, options: shuffle(q.options) };
            return;
        }
        const positions = q.options.map((_, i) => i);
        const least = Math.min(...positions.map((p) => usage[p]));
        const target = random(positions.filter((p) => usage[p] === least));
        usage[target] += 1;
        const others = shuffle(q.options.filter((_, i) => i !== q.correctIndex));
        const options = positions.map((p) => (p === target ? q.options[q.correctIndex] : others.shift() as string));
        result[qi] = { ...q, options, correctIndex: target };
    });
    return result;
};

export const correctLetterCounts = (questions: DraftQuestion[]) => {
    const maxOptions = Math.max(4, ...questions.map((q) => q.options.length));
    return OPTION_LETTERS.slice(0, maxOptions).map((letter, i) => ({
        letter,
        count: questions.filter((q) => q.correctIndex === i).length
    }));
};

const ensureAuth = async () => {
    if (!auth.currentUser) await signInAnonymously(auth);
};

export const subscribeQuizzes = (onData: (quizzes: Quiz[]) => void, onError: (error: Error) => void) => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    ensureAuth()
        .then(() => {
            if (cancelled) return;
            unsubscribe = onSnapshot(
                collection(db, QUIZZES_COLLECTION),
                (snapshot) => onData(
                    snapshot.docs
                        .map((document) => normalizeQuiz(document.id, document.data()))
                        .sort((a, b) => (b.quizDate?.getTime() ?? 0) - (a.quizDate?.getTime() ?? 0))
                ),
                onError
            );
        })
        .catch(onError);
    return () => {
        cancelled = true;
        if (unsubscribe) unsubscribe();
    };
};

export const getQuizWithKey = async (quizId: string) => {
    await ensureAuth();
    const [quizSnap, keySnap] = await Promise.all([
        getDoc(doc(db, QUIZZES_COLLECTION, quizId)),
        getDoc(doc(db, QUIZ_KEYS_COLLECTION, quizId))
    ]);
    if (!quizSnap.exists()) return null;
    return {
        quiz: normalizeQuiz(quizSnap.id, quizSnap.data()),
        key: (keySnap.exists() ? keySnap.data().answers : {}) as Record<string, string>
    };
};

export const getQuizKey = async (quizId: string) => {
    await ensureAuth();
    const snap = await getDoc(doc(db, QUIZ_KEYS_COLLECTION, quizId));
    return snap.exists() ? (snap.data().answers as Record<string, string>) : null;
};

export const countAttempts = async (quizId: string) => {
    await ensureAuth();
    const snap = await getCountFromServer(query(collection(db, QUIZ_COLLECTION), where("quizId", "==", quizId)));
    return snap.data().count;
};

// Solo puede haber un cuestionario activo: la web toma el que tenga status "active".
const deactivateOthers = async (batch: ReturnType<typeof writeBatch>, quizId: string) => {
    const active = await getDocs(query(collection(db, QUIZZES_COLLECTION), where("status", "==", "active")));
    active.docs.filter((document) => document.id !== quizId).forEach((document) => (
        batch.update(document.ref, { status: "inactive", updatedAt: serverTimestamp() })
    ));
};

export const saveQuiz = async (
    quizId: string | null, meta: QuizMeta, questions: DraftQuestion[],
    gradingScale: GradeRange[], status: QuizStatus
) => {
    await ensureAuth();
    const ref = quizId ? doc(db, QUIZZES_COLLECTION, quizId) : doc(collection(db, QUIZZES_COLLECTION));
    const quizDate = meta.quizDate.startOf("day");
    const batch = writeBatch(db);

    batch.set(ref, {
        title: meta.title.trim(),
        description: meta.description?.trim() ?? "",
        level: meta.level?.trim() ?? "",
        estimatedMinutes: meta.estimatedMinutes ?? null,
        quizDate: Timestamp.fromDate(quizDate.toDate()),
        quizDateKey: quizDate.format("YYYY-MM-DD"),
        status,
        questionCount: questions.length,
        gradingScale: [...gradingScale].sort((a, b) => b.min - a.min),
        questions: questions.map((q, i) => ({
            id: q.id,
            order: i + 1,
            text: q.text.trim(),
            options: q.options.map((text, oi) => ({ id: OPTION_LETTERS[oi], text: text.trim() }))
        })),
        updatedAt: serverTimestamp(),
        ...(quizId ? {} : { createdAt: serverTimestamp() })
    }, { merge: true });
    batch.set(doc(db, QUIZ_KEYS_COLLECTION, ref.id), {
        quizId: ref.id,
        answers: Object.fromEntries(questions.map((q) => [q.id, OPTION_LETTERS[q.correctIndex]])),
        updatedAt: serverTimestamp()
    });
    if (status === "active") await deactivateOthers(batch, ref.id);

    await batch.commit();
    return ref.id;
};

export const setQuizStatus = async (quizId: string, status: QuizStatus) => {
    await ensureAuth();
    const batch = writeBatch(db);
    batch.update(doc(db, QUIZZES_COLLECTION, quizId), { status, updatedAt: serverTimestamp() });
    if (status === "active") await deactivateOthers(batch, quizId);
    await batch.commit();
};

export const deleteQuiz = async (quizId: string) => {
    await ensureAuth();
    const batch = writeBatch(db);
    batch.delete(doc(db, QUIZZES_COLLECTION, quizId));
    batch.delete(doc(db, QUIZ_KEYS_COLLECTION, quizId));
    await batch.commit();
};
